import type {
  AddCompetitorRequest,
  AnalysisDetail,
  AnalysisListResponse,
  AuthMeResponse,
  CompanyProfile,
  ComparisonResponse,
  CompetitorsResponse,
  CreateAnalysisRequest,
  PatchProfileRequest,
  ReportSynthesisResponse,
  RunChangesResponse,
  SourcesResponse,
  UpdateUserRequest,
  UserSummary,
  UsersListResponse,
} from '@swg/contracts';

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Content-type alleen meesturen als er daadwerkelijk een body is: een bodyloze
  // POST (bijv. bevestigen) met content-type application/json wordt door Fastify
  // geweigerd ("Body cannot be empty when content-type is set to ...").
  const hasBody = init?.body !== undefined && init?.body !== null;
  const res = await fetch(path, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
    credentials: 'same-origin',
  });
  if (res.status === 401) {
    // Sessie verlopen: terug naar login.
    window.location.href = '/';
    throw new ApiError('Niet ingelogd.', 'UNAUTHENTICATED', 401);
  }
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const message = (data && data.message) || 'Er is een fout opgetreden.';
    const code = (data && data.code) || 'ERROR';
    throw new ApiError(message, code, res.status);
  }
  return data as T;
}

/** Haalt de huidige sessie op. */
export async function fetchAuthMe(): Promise<AuthMeResponse> {
  const res = await fetch('/api/auth/me', {
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Onverwachte status: ${res.status}`);
  return (await res.json()) as AuthMeResponse;
}

/** Start de Entra-login door de browser naar de API te sturen. */
export function startLogin(returnTo = '/'): void {
  const params = new URLSearchParams({ returnTo });
  window.location.href = `/api/auth/login?${params.toString()}`;
}

/** Meldt de lokale sessie af en herlaadt de app. */
export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  window.location.href = '/';
}

export function fetchAnalyses(): Promise<AnalysisListResponse> {
  return jsonFetch<AnalysisListResponse>('/api/analyses');
}

export function fetchAnalysis(id: string): Promise<AnalysisDetail> {
  return jsonFetch<AnalysisDetail>(`/api/analyses/${encodeURIComponent(id)}`);
}

export function createAnalysis(body: CreateAnalysisRequest): Promise<AnalysisDetail> {
  return jsonFetch<AnalysisDetail>('/api/analyses', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchProfile(runId: string): Promise<CompanyProfile> {
  return jsonFetch<CompanyProfile>(`/api/analysis-runs/${encodeURIComponent(runId)}/profile`);
}

export function patchProfile(runId: string, body: PatchProfileRequest): Promise<CompanyProfile> {
  return jsonFetch<CompanyProfile>(`/api/analysis-runs/${encodeURIComponent(runId)}/profile`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function confirmProfile(runId: string): Promise<CompanyProfile> {
  return jsonFetch<CompanyProfile>(
    `/api/analysis-runs/${encodeURIComponent(runId)}/profile/confirm`,
    { method: 'POST' },
  );
}

const competitorsBase = (runId: string) =>
  `/api/analysis-runs/${encodeURIComponent(runId)}/competitors`;

export function fetchCompetitors(runId: string): Promise<CompetitorsResponse> {
  return jsonFetch<CompetitorsResponse>(competitorsBase(runId));
}

export function setCandidateSelection(
  runId: string,
  candidateId: string,
  selected: boolean,
): Promise<CompetitorsResponse> {
  return jsonFetch<CompetitorsResponse>(
    `${competitorsBase(runId)}/${encodeURIComponent(candidateId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ selected }),
    },
  );
}

export function setCompetitorKvk(
  runId: string,
  candidateId: string,
  kvkNumber: string,
): Promise<CompetitorsResponse> {
  return jsonFetch<CompetitorsResponse>(
    `${competitorsBase(runId)}/${encodeURIComponent(candidateId)}/kvk`,
    {
      method: 'PATCH',
      body: JSON.stringify({ kvkNumber }),
    },
  );
}

export function addCompetitor(
  runId: string,
  body: AddCompetitorRequest,
): Promise<CompetitorsResponse> {
  return jsonFetch<CompetitorsResponse>(competitorsBase(runId), {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function removeCandidate(runId: string, candidateId: string): Promise<CompetitorsResponse> {
  return jsonFetch<CompetitorsResponse>(
    `${competitorsBase(runId)}/${encodeURIComponent(candidateId)}`,
    {
      method: 'DELETE',
    },
  );
}

export function confirmCompetitors(runId: string): Promise<CompetitorsResponse> {
  return jsonFetch<CompetitorsResponse>(`${competitorsBase(runId)}/confirm`, { method: 'POST' });
}

export function fetchUsers(): Promise<UsersListResponse> {
  return jsonFetch<UsersListResponse>('/api/users');
}

export function updateUser(id: string, body: UpdateUserRequest): Promise<UserSummary> {
  return jsonFetch<UserSummary>(`/api/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function fetchComparison(runId: string): Promise<ComparisonResponse> {
  return jsonFetch<ComparisonResponse>(
    `/api/analysis-runs/${encodeURIComponent(runId)}/comparison`,
  );
}

export function fetchSources(runId: string): Promise<SourcesResponse> {
  return jsonFetch<SourcesResponse>(`/api/analysis-runs/${encodeURIComponent(runId)}/sources`);
}

export function fetchRunChanges(runId: string): Promise<RunChangesResponse> {
  return jsonFetch<RunChangesResponse>(`/api/analysis-runs/${encodeURIComponent(runId)}/changes`);
}

export function fetchSynthesis(runId: string): Promise<ReportSynthesisResponse> {
  return jsonFetch<ReportSynthesisResponse>(
    `/api/analysis-runs/${encodeURIComponent(runId)}/synthesis`,
  );
}

/** Voert een bestaande analyse opnieuw uit (US10). */
export function rerunAnalysis(analysisId: string): Promise<AnalysisDetail> {
  return jsonFetch<AnalysisDetail>(`/api/analyses/${encodeURIComponent(analysisId)}/rerun`, {
    method: 'POST',
  });
}

/** Voert één stap (profiel/concurrenten/verzamelen) opnieuw uit (US10). */
export function restartStep(
  analysisId: string,
  step: 'profiel' | 'concurrenten' | 'verzamelen',
): Promise<AnalysisDetail> {
  return jsonFetch<AnalysisDetail>(
    `/api/analyses/${encodeURIComponent(analysisId)}/restart/${step}`,
    { method: 'POST' },
  );
}

/** Verwijdert een analyse en al haar uitvoeringen (onomkeerbaar). */
export async function deleteAnalysis(analysisId: string): Promise<void> {
  await jsonFetch<null>(`/api/analyses/${encodeURIComponent(analysisId)}`, { method: 'DELETE' });
}
